import React, { useState, useEffect, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  LayoutDashboard, Users, Package, ShoppingCart, TrendingUp,
  BarChart3, Settings, LogOut, Truck, Layers, FileText,
  Menu, X, Building, ChevronLeft, ChevronRight, Hash, CreditCard, Globe, ShoppingBag, Tag
} from 'lucide-react';

const NAV_ITEMS = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', perm: 'Dashboard' },
  { to: '/sales', icon: TrendingUp, label: 'Sales', perm: 'Sales' },
  { to: '/purchase', icon: ShoppingCart, label: 'Purchase', perm: 'Purchase' },
  { to: '/inventory', icon: Layers, label: 'Inventory', perm: 'Inventory' },
  { divider: true, label: 'WEBSITE' },
  { to: '/website-cms', icon: Globe, label: 'Website CMS', perm: 'Items' },
  { to: '/website-orders', icon: ShoppingBag, label: 'Website Orders', perm: 'Sales' },
  { to: '/coupons', icon: Tag, label: 'Coupons', perm: 'Sales' },
  { divider: true, label: 'MASTERS' },
  { to: '/customers', icon: Users, label: 'Customers', perm: 'Customers' },
  { to: '/suppliers', icon: Truck, label: 'Suppliers', perm: 'Suppliers' },
  { to: '/items', icon: Package, label: 'Items', perm: 'Items' },
  { to: '/branches', icon: Building, label: 'Branches', perm: 'Branches' },
  { to: '/sku-master', icon: Hash, label: 'SKU Master', perm: 'Branches' },
  { to: '/bank-settings', icon: CreditCard, label: 'Bank & Settings', perm: 'Users' },
  { divider: true, label: 'REPORTS' },
  { to: '/reports', icon: BarChart3, label: 'Reports', perm: 'Reports' },
  { to: '/gst', icon: FileText, label: 'GST', perm: 'GST' },
  { divider: true, label: 'ADMIN' },
  { to: '/users', icon: Settings, label: 'Users', perm: 'Users' },
  { to: '/audit-logs', icon: FileText, label: 'Audit Logs', perm: 'AuditLogs', superAdminOnly: true },
];

export default function Sidebar({ expanded, onToggle }) {
  const { user, logout } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hovering, setHovering] = useState(false);

  // Close mobile menu on resize
  useEffect(() => {
    const handler = () => { if (window.innerWidth > 768) setMobileOpen(false); };
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  const permissionsString = user?.permissions ||
    'Dashboard,Sales,Purchase,Inventory,Customers,Suppliers,Items,Reports,GST,Users,Branches';
  const userPerms = permissionsString.split(',').map(p => p.trim());

  const isSuperAdmin = user?.username?.toLowerCase() === 'mantracode' || user?.roleName === 'SuperAdmin';

  const filteredItems = NAV_ITEMS.filter((item, idx) => {
    if (item.superAdminOnly && !isSuperAdmin) {
      return false;
    }
    if (item.divider) {
      const rest = NAV_ITEMS.slice(idx + 1);
      const until = rest.findIndex(x => x.divider);
      const section = until === -1 ? rest : rest.slice(0, until);
      const sectionFiltered = section.filter(x => !x.superAdminOnly || isSuperAdmin);
      return sectionFiltered.some(x => isSuperAdmin ? true : userPerms.includes(x.perm));
    }
    // superAdminOnly items always visible to super admins
    if (item.superAdminOnly && isSuperAdmin) return true;
    return userPerms.includes(item.perm);
  });

  const isVisible = expanded || hovering;
  const initials = (user?.fullName || 'A').split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();

  const NavContent = ({ onLinkClick }) => (
    <div className="sb-inner">
      {/* Logo */}
      <div className="sb-logo">
        <div className="sb-logo-icon">
          <img src="/carat-quest-logo.png" alt="Carat Quest" className="sb-logo-img" style={{ objectFit: 'contain' }} />
        </div>
        <div className={`sb-logo-text${isVisible ? ' visible' : ''}`}>
          <span className="sb-brand">CARAT QUEST</span>
        </div>
        <button
          className={`sb-pin${isVisible ? ' visible' : ''}`}
          onClick={onToggle}
          title={expanded ? 'Collapse sidebar' : 'Pin sidebar'}
        >
          {expanded ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
        </button>
      </div>

      {/* Nav */}
      <nav className="sb-nav">
        {filteredItems.map((item, i) => {
          if (item.divider) {
            return (
              <div key={i} className={`sb-section${isVisible ? ' visible' : ''}`}>
                <span className="sb-section-label">{item.label}</span>
              </div>
            );
          }
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              className={({ isActive }) => `sb-link${isActive ? ' active' : ''}`}
              onClick={onLinkClick}
              title={!isVisible ? item.label : undefined}
            >
              <span className="sb-link-icon"><Icon size={18} strokeWidth={1.8} /></span>
              <span className={`sb-link-label${isVisible ? ' visible' : ''}`}>{item.label}</span>
              {!isVisible && <span className="sb-tooltip">{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="sb-footer">
        <div className="sb-avatar" title={user?.fullName}>{initials}</div>
        <div className={`sb-user-info${isVisible ? ' visible' : ''}`}>
          <div className="sb-user-name">{user?.fullName}</div>
          <div className="sb-user-role">{user?.roleName}</div>
        </div>
        <button
          className={`sb-logout${isVisible ? ' visible' : ''}`}
          onClick={logout}
          title="Logout"
        >
          <LogOut size={15} />
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile burger */}
      <button className="sb-mobile-btn" onClick={() => setMobileOpen(true)} aria-label="Open menu">
        <Menu size={20} />
      </button>

      {/* Mobile overlay */}
      {mobileOpen && <div className="sb-overlay" onClick={() => setMobileOpen(false)} />}

      {/* Desktop sidebar */}
      <aside
        className={`sb-desktop${expanded ? ' pinned' : ''}`}
        onMouseEnter={() => !expanded && setHovering(true)}
        onMouseLeave={() => !expanded && setHovering(false)}
      >
        <NavContent onLinkClick={undefined} />
      </aside>

      {/* Mobile sidebar */}
      <aside className={`sb-mobile${mobileOpen ? ' open' : ''}`}>
        <button className="sb-mobile-close" onClick={() => setMobileOpen(false)}>
          <X size={18} />
        </button>
        <NavContent onLinkClick={() => setMobileOpen(false)} />
      </aside>

      <style>{`
        /* ═══════════ BASE ═══════════ */
        .sb-desktop {
          position: fixed;
          top: 0; left: 0; bottom: 0;
          width: var(--sidebar-w-mini);
          background: #0d1f35;
          border-right: 1px solid rgba(255,255,255,0.06);
          z-index: 500;
          transition: width 0.25s cubic-bezier(0.4,0,0.2,1);
          overflow: hidden;
        }
        .sb-desktop:hover,
        .sb-desktop.pinned {
          width: var(--sidebar-w-full);
        }

        /* ═══════════ INNER ═══════════ */
        .sb-inner {
          display: flex;
          flex-direction: column;
          height: 100%;
          overflow: hidden;
          width: var(--sidebar-w-full);
        }

        /* ═══════════ LOGO ═══════════ */
        .sb-logo {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 18px 14px 16px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
          min-height: 64px;
        }

        .sb-logo-icon {
          width: 44px; height: 44px;
          min-width: 44px;
          background: transparent;
          border: none;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
          overflow: hidden;
          padding: 0;
        }

        .sb-logo-img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
        }

        .sb-logo-text {
          flex: 1;
          min-width: 0;
          opacity: 0;
          transform: translateX(-6px);
          transition: opacity 0.2s ease, transform 0.2s ease;
          white-space: nowrap;
          overflow: hidden;
        }
        .sb-logo-text.visible {
          opacity: 1;
          transform: translateX(0);
        }

        .sb-brand {
          display: block;
          font-size: 0.95rem;
          font-weight: 800;
          color: #fff;
          letter-spacing: 0.08em;
          line-height: 1.2;
          white-space: nowrap;
        }

        .sb-pin {
          opacity: 0;
          transition: opacity 0.2s;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 5px;
          color: rgba(255,255,255,0.45);
          padding: 3px;
          cursor: pointer;
          display: flex;
          align-items: center;
          flex-shrink: 0;
        }
        .sb-pin.visible { opacity: 1; }
        .sb-pin:hover { background: rgba(255,255,255,0.12); color: #fff; }

        /* ═══════════ NAV ═══════════ */
        .sb-nav {
          flex: 1;
          padding: 10px 8px;
          display: flex;
          flex-direction: column;
          gap: 1px;
          overflow-y: auto;
          overflow-x: hidden;
        }
        .sb-nav::-webkit-scrollbar { width: 3px; }
        .sb-nav::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); }

        .sb-section {
          padding: 10px 4px 4px;
          overflow: hidden;
        }
        .sb-section-label {
          display: block;
          font-size: 0.58rem;
          font-weight: 800;
          letter-spacing: 0.14em;
          color: rgba(255,255,255,0.22);
          padding: 0 8px;
          white-space: nowrap;
          opacity: 0;
          transition: opacity 0.2s ease;
        }
        .sb-section.visible .sb-section-label { opacity: 1; }

        /* Nav link */
        .sb-link {
          position: relative;
          display: flex;
          align-items: center;
          gap: 11px;
          padding: 9px 12px;
          border-radius: 8px;
          color: rgba(255,255,255,0.48);
          font-size: 0.875rem;
          font-weight: 500;
          transition: background 0.15s, color 0.15s;
          white-space: nowrap;
          overflow: visible;
          text-decoration: none;
        }

        .sb-link-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          min-width: 20px;
          flex-shrink: 0;
          transition: color 0.15s;
        }

        .sb-link-label {
          opacity: 0;
          transform: translateX(-4px);
          transition: opacity 0.18s ease, transform 0.18s ease;
          white-space: nowrap;
          flex: 1;
        }
        .sb-link-label.visible {
          opacity: 1;
          transform: translateX(0);
        }

        /* Tooltip when collapsed */
        .sb-tooltip {
          display: none;
          position: absolute;
          left: calc(var(--sidebar-w-mini) + 8px);
          top: 50%;
          transform: translateY(-50%);
          background: #1e3a5f;
          color: #fff;
          font-size: 0.8rem;
          font-weight: 500;
          padding: 5px 10px;
          border-radius: 6px;
          white-space: nowrap;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3);
          pointer-events: none;
          z-index: 9999;
        }
        .sb-tooltip::before {
          content: '';
          position: absolute;
          right: 100%;
          top: 50%;
          transform: translateY(-50%);
          border: 5px solid transparent;
          border-right-color: #1e3a5f;
        }
        .sb-link:hover .sb-tooltip { display: block; }
        .sb-desktop:hover .sb-link .sb-tooltip,
        .sb-desktop.pinned .sb-link .sb-tooltip { display: none !important; }

        .sb-link:hover {
          background: rgba(255,255,255,0.07);
          color: rgba(255,255,255,0.9);
        }

        .sb-link.active {
          background: rgba(255,255,255,0.1);
          color: #ffffff;
          font-weight: 600;
        }
        .sb-link.active .sb-link-icon { color: #ffffff; }
        .sb-link.active::before {
          content: '';
          position: absolute;
          left: 0; top: 4px; bottom: 4px;
          width: 3px;
          background: rgba(255,255,255,0.7);
          border-radius: 0 3px 3px 0;
        }

        /* ═══════════ FOOTER ═══════════ */
        .sb-footer {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 12px 12px 14px;
          border-top: 1px solid rgba(255,255,255,0.06);
          background: transparent;
          overflow: hidden;
          min-height: 56px;
        }

        .sb-avatar {
          width: 34px; height: 34px;
          min-width: 34px;
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.2);
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          color: #fff;
          font-weight: 700;
          font-size: 0.8rem;
          flex-shrink: 0;
        }

        .sb-user-info {
          flex: 1;
          min-width: 0;
          opacity: 0;
          transform: translateX(-4px);
          transition: opacity 0.18s ease, transform 0.18s ease;
          white-space: nowrap;
          overflow: hidden;
        }
        .sb-user-info.visible { opacity: 1; transform: translateX(0); }
        .sb-user-name { font-size: 0.8rem; font-weight: 600; color: rgba(255,255,255,0.9); overflow: hidden; text-overflow: ellipsis; }
        .sb-user-role { font-size: 0.68rem; color: rgba(255,255,255,0.35); }

        .sb-logout {
          opacity: 0;
          transition: opacity 0.18s, color 0.15s, background 0.15s;
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.35);
          padding: 6px;
          border-radius: 7px;
          display: flex; align-items: center;
          cursor: pointer;
          flex-shrink: 0;
        }
        .sb-logout.visible { opacity: 1; }
        .sb-logout:hover { color: #f87171; background: rgba(239,68,68,0.12); }

        /* ═══════════ MOBILE ═══════════ */
        .sb-mobile-btn {
          display: none;
          position: fixed;
          top: 12px; left: 12px;
          z-index: 600;
          background: #0d1f35;
          border: 1px solid rgba(255,255,255,0.1);
          color: #fff;
          padding: 8px;
          border-radius: 8px;
          cursor: pointer;
          align-items: center;
          justify-content: center;
        }

        .sb-overlay {
          display: none;
          position: fixed; inset: 0;
          background: rgba(0,0,0,0.5);
          z-index: 590;
          backdrop-filter: blur(2px);
        }

        .sb-mobile {
          display: none;
          position: fixed;
          top: 0; left: 0; bottom: 0;
          width: var(--sidebar-w-full);
          background: #0d1f35;
          border-right: 1px solid rgba(255,255,255,0.06);
          z-index: 600;
          transform: translateX(-100%);
          transition: transform 0.28s cubic-bezier(0.4,0,0.2,1);
          flex-direction: column;
        }
        .sb-mobile.open { transform: translateX(0); }

        .sb-mobile-close {
          position: absolute;
          top: 14px; right: 14px;
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.45);
          cursor: pointer;
          padding: 6px;
          border-radius: 6px;
        }
        .sb-mobile-close:hover { color: #fff; background: rgba(255,255,255,0.08); }

        /* Mobile: always show labels */
        .sb-mobile .sb-logo-text,
        .sb-mobile .sb-link-label,
        .sb-mobile .sb-user-info,
        .sb-mobile .sb-logout,
        .sb-mobile .sb-pin {
          opacity: 1 !important;
          transform: none !important;
        }
        .sb-mobile .sb-section-label { opacity: 1 !important; }

        @media (max-width: 768px) {
          .sb-desktop { display: none; }
          .sb-mobile { display: flex; }
          .sb-overlay { display: block; }
          .sb-mobile-btn { display: flex; }
        }
      `}</style>
    </>
  );
}
